import React, { useState } from 'react';
import { Zap } from 'lucide-react';

const FRICTION_LABELS = {
  1: 'Collegial', 2: 'Collegial', 3: 'Professional', 4: 'Professional',
  5: 'Assertive', 6: 'Assertive', 7: 'Adversarial', 8: 'Adversarial',
  9: 'Combative', 10: 'Maximum'
};
const FRICTION_COLORS = {
  1: '#22C55E', 2: '#22C55E', 3: '#F59E0B', 4: '#F59E0B',
  5: '#FF4D00', 6: '#FF4D00', 7: '#FF6B00', 8: '#FF6B00',
  9: '#EF4444', 10: '#EF4444'
};

const DEBATE_MODES = [
  { id: 'panel', name: 'Panel', desc: 'All voices deliberate simultaneously. Fast.', badge: 'FREE' },
  { id: 'round_robin', name: 'Round-Robin', desc: 'Voices take turns with live context. Deeper.', badge: 'PRO' },
  { id: 'dialectic', name: 'Dialectic', desc: 'Two voices go head-to-head, others judge.', badge: 'PRO' },
  { id: 'cross_examination', name: 'Cross-Exam', desc: 'Each voice challenged individually.', badge: 'TEAM' },
  { id: 'cat_fight', name: 'Cat Fight', desc: 'No-holds-barred AI argument. You can heckle. Pure entertainment.', badge: 'NEW', color: '#EF4444' },
];

const CAT_FIGHT_THEMES = [
  { id: 'boxing_ring', name: 'Boxing Ring', desc: 'Dark, dramatic. Two fighters in the spotlight.' },
  { id: 'live_stream', name: 'Live Stream', desc: 'Twitch-style chat. Fast and chaotic.' },
  { id: 'wrestling', name: 'Wrestling', desc: 'Over-the-top announcer style. Maximum drama.' },
];

export function DebateModeSelector({ value, onChange }) {
  return (
    <div>
      <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-3">Debate Mode</h3>
      <div className="space-y-2">
        {DEBATE_MODES.map(m => (
          <button key={m.id} onClick={() => onChange(m.id)}
            className={`w-full p-3 min-h-[44px] text-left border transition-colors ${value === m.id
              ? m.color ? `border-[${m.color}] bg-[${m.color}]/5` : 'border-[#FF4D00] bg-[#FF4D00]/5'
              : 'border-[#2A2A2A] hover:border-[#FF4D00]/30'}`}
            style={value === m.id && m.color ? { borderColor: m.color, backgroundColor: m.color + '0D' } : {}}
            data-testid={`mode-${m.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium font-['Barlow_Condensed']">{m.name}</span>
              <span className="text-[8px] px-1.5 py-0.5 border font-mono"
                style={m.color ? { color: m.color, borderColor: m.color + '40' } : { color: '#A0A0A0', borderColor: '#2A2A2A' }}>
                {m.badge}
              </span>
            </div>
            <p className="text-xs text-[#A0A0A0] mt-1">{m.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function CatFightThemeSelector({ value, onChange }) {
  return (
    <div>
      <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-3">Theater Theme</h3>
      <div className="space-y-2">
        {CAT_FIGHT_THEMES.map(t => (
          <button key={t.id} onClick={() => onChange(t.id)}
            className={`w-full p-3 min-h-[44px] text-left border transition-colors ${value === t.id ? 'border-[#EF4444] bg-[#EF4444]/5' : 'border-[#2A2A2A] hover:border-[#EF4444]/30'}`}
            data-testid={`theme-${t.id}`}>
            <span className="text-sm font-medium font-['Barlow_Condensed']">{t.name}</span>
            <p className="text-xs text-[#A0A0A0] mt-1">{t.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function FrictionSlider({ value, onChange }) {
  const color = FRICTION_COLORS[value] || '#F59E0B';
  const label = FRICTION_LABELS[value] || 'Professional';

  return (
    <div>
      <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-3">Friction Level</h3>
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-3xl font-bold font-['Barlow_Condensed']" style={{ color }}>{value}</span>
          <span className="text-sm font-mono" style={{ color }}>{label}</span>
        </div>
        <input type="range" min="1" max="10" value={value} onChange={e => onChange(parseInt(e.target.value))}
          className="w-full accent-[#FF4D00] h-2 min-h-[44px]" data-testid="friction-slider" />
        <div className="flex justify-between text-[8px] text-[#A0A0A0] font-mono mt-1">
          <span>Collegial</span><span>Professional</span><span>Adversarial</span><span>Max</span>
        </div>
      </div>
    </div>
  );
}

export function DebateConfig({ debateMode, setDebateMode, frictionLevel, setFrictionLevel, catFightTheme, setCatFightTheme }) {
  const isCatFight = debateMode === 'cat_fight';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
      <DebateModeSelector value={debateMode} onChange={setDebateMode} />
      {isCatFight ? (
        <div>
          <CatFightThemeSelector value={catFightTheme || 'boxing_ring'} onChange={setCatFightTheme} />
          <div className="mt-3 p-3 bg-[#EF4444]/5 border border-[#EF4444]/20">
            <p className="text-[10px] text-[#EF4444] font-mono">Friction: MAXIMUM (locked). Runs until you stop. You can heckle mid-fight.</p>
          </div>
        </div>
      ) : (
        <FrictionSlider value={frictionLevel} onChange={setFrictionLevel} />
      )}
    </div>
  );
}
